import { groupBy } from "lodash";
import { put, select, takeEvery } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { importFileFailed, importFileSucceeded } from "actions";

import { CLEARING_SALE_PRE_SALE_CSV } from "constants/actionTypes";

import { downloadFileBlob, EMPTY_ARRAY } from "lib";

import { getLivestockSaleId } from "lib/navigation";

import { getActiveRoleDeployments, getCurrentSale } from "selectors";

import toast from "lib/toast";
import { SellSafeCategory } from "constants/clearingSaleAttributes";
import { SaleType } from "constants/sale";
import { api } from "../api";
import {
  buildLotsAndErrorsListFromCsvImport,
  onDefaultFileSuccess,
} from "./util";

interface ClearingSalePreSaleCsvLot {
  lotNumber: string;
  title: string;
  description: string;
  quantity: string;
  category: string;
  startBid: string;
  reservePrice: string;
  bidIncrement: string;
  vendorName: string;
  vendorCommission: string;
  vendorNote: string;
  gstExempt: boolean;
  hours: string;
  inspection: string;
  make: string;
  model: string;
  newUsed: string;
  odometer: string;
  registration: string;
  sellSafeCategory: string;
  subType: string;
  type: string;
  vin: string;
  year: string;
  comments: string;
  notes: string;
  ppsrComments: string;
  ppsrStatus: string;
}

const buildVendorNoteByVendorNameAndCommission = vendorGroups =>
  // building a mapping of vendor names to an array of their unique notes (receiving references)
  Object.values(vendorGroups).reduce((acc, vendorGroup) => {
    for (const lotData of vendorGroup as ClearingSalePreSaleCsvLot[]) {
      const { vendorName, vendorNote, vendorCommission } = lotData;
      const key = `${vendorName}__${vendorCommission}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      if (vendorNote && !acc[key].includes(vendorNote)) {
        acc[key].push(vendorNote);
      }
    }
    return acc;
  }, {});

const validateStringParseInt = (value: string | undefined) => {
  if (value) {
    const parsed = parseInt(value, 10);
    return !Number.isNaN(parsed);
  }
  return true;
};

const clearingSaleValidationSchema = Yup.object().shape({
  lotNumber: Yup.string().required(),
  title: Yup.string().required(),
  description: Yup.string(),
  quantity: Yup.string()
    .required()
    .test(
      "quantity",
      "Invalid Quantity",
      value => !value || !Number.isNaN(parseInt(value, 10)),
    ),
  category: Yup.string(),
  startBid: Yup.string().test("startBid", "Invalid Starting Bid", value =>
    validateStringParseInt(value),
  ),
  reservePrice: Yup.string().test(
    "reservePrice",
    "Invalid Reserve Price",
    value => validateStringParseInt(value),
  ),
  bidIncrement: Yup.string().test(
    "bidIncrement",
    "Invalid Bid Increment",
    value => validateStringParseInt(value),
  ),
  vendorName: Yup.string().required(),
  vendorCommission: Yup.string(),
  // vendorNote in clearing sales is actually pointing to the receiving_reference on the consignment.
  // the consignment modal ui is showing the receiving reference as vendor note to the user.
  vendorNote: Yup.string().max(
    100,
    "Vendor note cannot be longer than 100 characters",
  ),
  gstExempt: Yup.boolean()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        const normalized = originalValue.trim().toLowerCase();
        if (["true", "yes", "y", "1"].includes(normalized)) {
          return true;
        }
        if (["false", "no", "n", "0", ""].includes(normalized)) {
          return false;
        }
      }
      return value;
    })
    .typeError("gstExempt Must be a boolean value (true/yes/1 or false/no/0)")
    .required("This field is required"),
  hours: Yup.string().test("hours", "Invalid Hours", value =>
    validateStringParseInt(value),
  ),
  inspection: Yup.string(),
  make: Yup.string(),
  model: Yup.string(),
  newUsed: Yup.string(),
  odometer: Yup.string().test("odometer", "Invalid Odometer", value =>
    validateStringParseInt(value),
  ),
  registration: Yup.string(),
  sellSafeCategory: Yup.string().test(
    "Sell Safe Category",
    "Invalid Sell Safe Category - choose 1 Green, 2 Orange, 3 Red or Empty",
    value =>
      // value must be one of the defined SellSafeCategory values or empty
      !value || Object.values<string>(SellSafeCategory).includes(value),
  ),
  subType: Yup.string(),
  type: Yup.string(),
  vin: Yup.string(),
  year: Yup.string(),
  comments: Yup.string(),
  notes: Yup.string(),
  ppsrComments: Yup.string(),
  ppsrStatus: Yup.string(),
});

function* onProcessClearingSalePreSaleCSV(action) {
  const { file, fileContents } = action;
  const state = yield select();
  const currentSale = getCurrentSale(state);

  const { lots, errors } = buildLotsAndErrorsListFromCsvImport(
    fileContents,
    clearingSaleValidationSchema,
  );

  const hasErrorMessage = errors.length > 0;
  if (hasErrorMessage) {
    yield put(importFileFailed({}, CLEARING_SALE_PRE_SALE_CSV));
    let errorMessage = "";
    if (errors.length > 4) {
      errorMessage = errors
        .slice(0, 4)
        .map(error => `Line ${error.line}: ${error.error}`)
        .join(". \n");
      errorMessage += `. Plus ${errors.length - 4} more issues.`;
    } else {
      errorMessage = errors
        .map(error => `Line ${error.line}: ${error.error}`)
        .join(". \n");
    }
    toast.error(errorMessage);
    return;
  }

  const vendorMap = new Map();

  const vendorGroups = groupBy(lots, "vendorName");

  const existingVendorMapping =
    state.importers.clearingSalePreSaleCsv?.vendors || [];

  Object.values(vendorGroups).forEach(vendorLots => {
    const vendorId =
      existingVendorMapping.find(
        existingVendor =>
          existingVendor.vendorName === vendorLots[0].vendorName,
      )?.vendorId || null;
    vendorMap.set(vendorLots[0].vendorName, {
      id: uuidv4(),
      vendorId,
      vendorName: vendorLots[0].vendorName,
      vendorCommission: vendorLots[0].vendorCommission,
    });
  });

  const payload = {
    file: {
      name: file.name,
      lastModified: file.lastModified,
      type: file.type,
      rawContent: fileContents,
      vendorGroups,
      sale: {
        saleDate: currentSale.date,
        saleyardId: currentSale.id,
      },
    },
    isDataLossAcknowledged: true,
    vendors: Array.from(vendorMap.values()),
  };

  yield put({ type: CLEARING_SALE_PRE_SALE_CSV.UPDATE.REQUEST, payload });
}

function* onImportClearingSalePreSaleCsv(action) {
  const { replaceData } = action;
  const state = yield select();
  const deploymentId = getActiveRoleDeployments(state)[0].id;

  const {
    file: { vendorGroups },
    vendors,
  } = state.importers.clearingSalePreSaleCsv;

  const vendorNoteByVendorNameAndCommission =
    buildVendorNoteByVendorNameAndCommission(vendorGroups);

  const consignmentMap = new Map();

  for (const vendorGroup of Object.values(vendorGroups)) {
    for (const lotData of vendorGroup as ClearingSalePreSaleCsvLot[]) {
      const { vendorName, vendorCommission } = lotData;
      const { vendorId } = vendors.find(
        vendorMapping => vendorMapping.vendorName === vendorName,
      );

      const consignmentKey = `${vendorId}__${vendorCommission}`;
      if (!consignmentMap.has(consignmentKey)) {
        consignmentMap.set(consignmentKey, {
          quantity: 1,
          sale_lots: [],
          vendor: { cbid: vendorId },
          receiving_reference: vendorNoteByVendorNameAndCommission[
            `${vendorName}__${vendorCommission}`
          ]
            .join(", ")
            .slice(0, 100),
          vendor_commission: parseFloat(vendorCommission),
        });
      }

      const consignment = consignmentMap.get(consignmentKey);
      const quantity = +parseInt(lotData.quantity, 10);

      const match = /(\d+)(.*)/.exec(lotData.lotNumber);

      const lotNumber = match[1];
      const lotNumberSuffix = match[2];

      const saleLot = {
        lot_number: lotNumber || lotData.lotNumber,
        lot_number_suffix: lotNumberSuffix || null,
        quantity,
        is_gst_exempt: lotData.gstExempt || false,
        bid_increment: lotData.bidIncrement || 0,
        reserve_price: lotData.reservePrice || 0,
        starting_bid: lotData.startBid || 0,
        note: lotData.notes,
        clearing_sale_attributes: {
          cbid: uuidv4(),
          title: lotData.title,
          category: lotData.category,
          public_description: lotData.description,
          comments: lotData.comments,
          inspection: lotData.inspection,
          new_used: lotData.newUsed,
          year: lotData.year || null,
          item_type: lotData.type,
          sub_type: lotData.subType,
          make: lotData.make,
          model: lotData.model,
          hours: lotData.hours || 0,
          odometer: lotData.odometer || 0,
          vin: lotData.vin,
          registration: lotData.registration,
          ppsr_status: lotData.ppsrStatus,
          ppsr_comments: lotData.ppsrComments,
          sell_safe_category: lotData.sellSafeCategory || null,
        },
      };

      consignment.sale_lots.push(saleLot);
    }
  }

  const payload = {
    id: getLivestockSaleId(),
    deployment_sales: [
      {
        deployment_id: deploymentId,
        consignments: Array.from(consignmentMap.values()),
      },
    ],
  };

  yield put({
    type: CLEARING_SALE_PRE_SALE_CSV.IMPORT.REQUEST,
    payload,
    replaceData,
  });
}

function* onUpdateClearingSalePreSaleCsv(action) {
  const state = yield select();
  const payload = { ...action.payload };

  const storedVendorMapping =
    state.importers.clearingSalePreSaleCsv?.storedMapping.vendors || {};

  // update payload vendor IDs based on stored mapping if available and not set.
  payload.vendors?.forEach(vendor => {
    if (!vendor.vendorId) {
      const storedVendorId =
        storedVendorMapping[vendor.vendorName.toLowerCase()]?.vendorId;
      if (storedVendorId) {
        vendor.vendorId = storedVendorId;
      }
    }
  });

  yield put({ type: CLEARING_SALE_PRE_SALE_CSV.UPDATE.ACTION, payload });
}

function* onClearingSalePreSaleCsvRequest({ payload, replaceData = true }) {
  try {
    const url = `/v2/sales/${payload.id}/import/?keep_data=${!replaceData}`;
    const response = yield api.put(url, payload);
    yield put(
      importFileSucceeded(
        response,
        CLEARING_SALE_PRE_SALE_CSV,
        EMPTY_ARRAY,
        SaleType.CLEARING,
      ),
    );
  } catch (e) {
    yield put(importFileFailed(e, CLEARING_SALE_PRE_SALE_CSV));
  }
}

function onDownloadClearingSalePreSaleCsvSampleRequest({ livestockSaleId }) {
  const data = `${Object.keys(clearingSaleValidationSchema.fields).join(",")}\n`;

  downloadFileBlob(
    `${livestockSaleId}_AgriNousClearingSaleCsvSaleImport.csv`,
    new Blob([data], { type: "text/csv" }),
  );
}

export function* clearingSalePreSaleCsvImportSagas() {
  yield takeEvery(
    CLEARING_SALE_PRE_SALE_CSV.PROCESS.ACTION,
    onProcessClearingSalePreSaleCSV,
  );
  yield takeEvery(
    CLEARING_SALE_PRE_SALE_CSV.IMPORT.ACTION,
    onImportClearingSalePreSaleCsv,
  );
  yield takeEvery(
    CLEARING_SALE_PRE_SALE_CSV.IMPORT.REQUEST,
    onClearingSalePreSaleCsvRequest,
  );
  yield takeEvery(
    CLEARING_SALE_PRE_SALE_CSV.UPDATE.REQUEST,
    onUpdateClearingSalePreSaleCsv,
  );
  yield takeEvery(
    CLEARING_SALE_PRE_SALE_CSV.IMPORT.SUCCESS,
    onDefaultFileSuccess,
  );
  yield takeEvery(
    CLEARING_SALE_PRE_SALE_CSV.DOWNLOAD_SAMPLE.REQUEST,
    onDownloadClearingSalePreSaleCsvSampleRequest,
  );
}
